import { DOC_SEARCH_DWELL } from 'features/analytics'
import { ChatbotSidePanel, useChatbotAttachments } from 'features/chatbot'
import { ContextMenu } from 'features/knowledgeDocumentViewer/components/ContextMenu'
import { CantileverPageHostsProvider } from 'features/knowledgeDocumentViewer/contexts/CantileverPageHostsContext'
import { DocumentTabDataProvider } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { SearchActionsProvider } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { SearchResultsNavigationProvider } from 'features/knowledgeDocumentViewer/contexts/SearchResultsNavigationContext'
import { useTimeSeriesLinkContext } from 'features/knowledgeDocumentViewer/contexts/TimeSeriesLinkContext'
import { useFavoriteDocuments } from 'features/knowledgeDocumentViewer/hooks/useFavoriteDocuments'
import type { IntraDocSearchUiState } from 'features/knowledgeDocumentViewer/hooks/useIntraDocumentSearch'
import { LinkedDocumentsNavigationResult } from 'features/knowledgeDocumentViewer/hooks/useLinkedDocumentsNavigation'
import {
  type SendPowerAnalyticsEvent,
  usePowerAnalyticsBackend,
} from 'features/knowledgeDocumentViewer/hooks/usePowerAnalyticsBackend'
import { useRecentItems } from 'features/knowledgeDocumentViewer/hooks/useRecentItems'
import {
  DocumentHistoryItem,
  DocumentTab,
  SearchHistoryItem,
  TimeSeriesHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import type { ChartSearchItem } from 'features/knowledgeDocumentViewer/types/searchableItems'
import {
  getPersistedChatbotState,
  persistChatbotState,
} from 'features/knowledgeDocumentViewer/utils/documentPageSessionStorage'
import { linkedDocsNavigationToRecentAsset } from 'features/knowledgeDocumentViewer/utils/recentItemsStorage'
import {
  chartSaveTabLabel,
  timeSeriesTabDefaultName,
} from 'features/knowledgeDocumentViewer/utils/timeSeriesTabName'
import { getWorkspaceTabLabel } from 'features/workspaceTabs/getWorkspaceTabLabel'
import { useWorkspaceTabTitle } from 'features/workspaceTabs/useWorkspaceTabTitle'
import { useOptionalWorkspaceTabs } from 'features/workspaceTabs/WorkspaceTabsContext'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DocumentTableRow } from './advancedSearch/components/documentsTableView/DocumentTableColumns'
import { AdvancedSearchPage } from './advancedSearch/components/search/AdvancedSearchPage'
import {
  type EmbeddedChartSavedInfo,
  EmbeddedTimeSeriesChart,
} from './dashboard/EmbeddedTimeSeriesChart'
import { BoxContextMenuState, DocumentTabContent } from './DocumentTabContent'
import {
  DocumentContentWrapper,
  EditorContainer,
  EditorMainColumn,
  KeepAlivePanel,
  MainContentWrapper,
  TabContentPanel,
  TabTransitionOverlay,
  ViewerContainer,
} from './knowledgeDocumentToolsStyles'
import { DocumentTabsBar } from './toolbar/DocumentTabsBar'

export function KnowledgeDocumentTools({
  scale = 1.5,
}: {
  scale?: number
  fitOnLoad?: boolean
}) {
  // Provider presence gates legacy DocumentTabs; delete with workspace rollout.
  const workspaceTabs = useOptionalWorkspaceTabs()
  const {
    siteId: siteID,
    activeTabId,
    openTabs,
    currentDocumentId,
    currentSearchOriginQuery,
    updateDocumentHistoryItem,
    updateTimeSeriesHistoryItem,
    renameTab,
    isGeckoUser,
  } = useKnowledgeDocumentUXContext()

  const { hasTimeSeriesData, getMatchedDatapoints } = useTimeSeriesLinkContext()
  const { recentItems, addRecentItem } = useRecentItems(siteID)
  const { isFavorited, toggleFavorite } = useFavoriteDocuments(siteID)
  const { sendPowerAnalyticsEvent } = usePowerAnalyticsBackend(siteID)
  const { systemDocumentEntries } = useKnowledgeDocumentDataContext()

  const [isChatbotOpen, setIsChatbotOpen] = useState(
    () => getPersistedChatbotState(siteID)?.isChatbotOpen ?? false
  )

  const dwellRef = useRef<{
    documentId: string
    startedAt: number
    query: string
  } | null>(null)
  const dwellAnalyticsRef = useRef<{
    sendEvent: SendPowerAnalyticsEvent
    siteId: string
  }>({
    sendEvent: sendPowerAnalyticsEvent,
    siteId: siteID,
  })
  dwellAnalyticsRef.current = {
    sendEvent: sendPowerAnalyticsEvent,
    siteId: siteID,
  }

  const currentDocRef = useRef({
    id: currentDocumentId,
    query: currentSearchOriginQuery,
  })
  currentDocRef.current = {
    id: currentDocumentId,
    query: currentSearchOriginQuery,
  }

  const fireDwell = useCallback((options?: { keepalive?: boolean }) => {
    if (!dwellRef.current) return
    const duration = Math.round(performance.now() - dwellRef.current.startedAt)
    const { sendEvent, siteId: site } = dwellAnalyticsRef.current
    if (duration >= 1000) {
      const dwellPayload = {
        document_id: dwellRef.current.documentId,
        dwell_duration_ms: duration,
        query: dwellRef.current.query,
        site_id: site,
      }
      sendEvent(DOC_SEARCH_DWELL.title, dwellPayload, options)
    }
    dwellRef.current = null
  }, [])

  const fireDwellBeacon = useCallback(
    () => fireDwell({ keepalive: true }),
    [fireDwell]
  )

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'hidden') {
      fireDwellBeacon()
    } else if (document.visibilityState === 'visible') {
      const { id, query } = currentDocRef.current
      if (id && !dwellRef.current) {
        dwellRef.current = {
          documentId: id,
          startedAt: performance.now(),
          query,
        }
      }
    }
  }, [fireDwellBeacon])

  useEffect(() => {
    fireDwell()

    if (currentDocumentId) {
      dwellRef.current = {
        documentId: currentDocumentId,
        startedAt: performance.now(),
        query: currentDocRef.current.query,
      }
    }

    return () => fireDwell()
  }, [currentDocumentId, fireDwell])

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', fireDwellBeacon)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', fireDwellBeacon)
    }
  }, [handleVisibilityChange, fireDwellBeacon])

  const attachmentDoc = useMemo(
    () =>
      systemDocumentEntries.find(
        (documentEntry) => documentEntry.entry.id === currentDocumentId
      ),
    [currentDocumentId, systemDocumentEntries]
  )
  const { addAttachment } = useChatbotAttachments()

  const handleLinkedDocsNavigationSuccess = useCallback(
    (result: LinkedDocumentsNavigationResult) => {
      const asset = linkedDocsNavigationToRecentAsset(result)
      if (asset) addRecentItem(asset)
    },
    [addRecentItem]
  )

  const [contextMenuState, setContextMenuState] =
    useState<BoxContextMenuState | null>(null)
  const [mountedSearchTabIds, setMountedSearchTabIds] = useState<Set<string>>(
    () => new Set()
  )

  const searchResultsByTabRef = useRef<Map<string, DocumentTableRow[]>>(
    new Map()
  )

  const intraSearchStateByTabRef = useRef<Map<string, IntraDocSearchUiState>>(
    new Map()
  )

  const handleBoxContextMenuChange = useCallback(
    (state: BoxContextMenuState | null) => {
      setContextMenuState(state)
    },
    []
  )

  const handleSnapshotSearchResults = useCallback(
    (tabId: string, results: DocumentTableRow[]) => {
      searchResultsByTabRef.current.set(tabId, [...results])
    },
    []
  )

  const getSearchResultsForTab = useCallback(
    (tabId: string) => searchResultsByTabRef.current.get(tabId),
    []
  )

  const getIntraSearchStateForTab = useCallback(
    (tabId: string) => intraSearchStateByTabRef.current.get(tabId),
    []
  )

  const handleIntraSearchStateChange = useCallback(
    (tabId: string, state: IntraDocSearchUiState) => {
      intraSearchStateByTabRef.current.set(tabId, state)
    },
    []
  )

  const handleNavigateToSearchResult = useCallback(
    (documentId: string) => {
      updateDocumentHistoryItem(activeTabId, {
        documentId,
        pageNumber: 1,
        selectedBoxIds: [],
      })
    },
    [activeTabId, updateDocumentHistoryItem]
  )

  const activeTabRenderData = useMemo(() => {
    const activeTab =
      openTabs.find((tab) => tab.id === activeTabId) ?? openTabs[0]
    if (!activeTab) return null

    const historyItem = activeTab.history[activeTab.currentIndex]
    return { tab: activeTab, historyItem }
  }, [openTabs, activeTabId])

  const renderDocumentTab = useCallback(
    (tab: DocumentTab, historyItem: DocumentHistoryItem) => {
      const tabSearchResults = getSearchResultsForTab(tab.id)
      const showNav =
        isGeckoUser && tabSearchResults && tabSearchResults.length > 0

      const content = (
        <CantileverPageHostsProvider
          documentId={historyItem.documentId}
          siteID={siteID}>
          <DocumentTabDataProvider
            documentId={historyItem.documentId}
            pageNumber={historyItem.pageNumber}
            siteID={siteID}>
            <DocumentTabContent
              documentId={historyItem.documentId}
              pageNumber={historyItem.pageNumber}
              selectedBoxIds={historyItem.selectedBoxIds}
              selectedSubjectUid={historyItem.selectedSubjectUid}
              scale={scale}
              isActiveTab
              hasTimeSeriesData={hasTimeSeriesData}
              getMatchedDatapoints={getMatchedDatapoints}
              setIsChatbotOpen={setIsChatbotOpen}
              onBoxContextMenuChange={handleBoxContextMenuChange}
              linkedDocsNavigationOptions={{
                onNavigationSuccess: handleLinkedDocsNavigationSuccess,
              }}
              fragmentHighlight={historyItem.fragmentHighlight}
              regionHighlight={historyItem.regionHighlight}
              initialIntraSearchState={getIntraSearchStateForTab(tab.id)}
              onIntraSearchStateChange={(state) =>
                handleIntraSearchStateChange(tab.id, state)
              }
            />
          </DocumentTabDataProvider>
        </CantileverPageHostsProvider>
      )

      return (
        <TabContentPanel key={tab.id}>
          {showNav ? (
            <SearchResultsNavigationProvider
              resultDocuments={tabSearchResults}
              currentDocumentId={historyItem.documentId}
              onNavigateToDocument={handleNavigateToSearchResult}>
              {content}
            </SearchResultsNavigationProvider>
          ) : (
            content
          )}
        </TabContentPanel>
      )
    },
    [
      isGeckoUser,
      siteID,
      scale,
      hasTimeSeriesData,
      getMatchedDatapoints,
      getSearchResultsForTab,
      getIntraSearchStateForTab,
      handleIntraSearchStateChange,
      handleBoxContextMenuChange,
      handleLinkedDocsNavigationSuccess,
      handleNavigateToSearchResult,
      setIsChatbotOpen,
    ]
  )

  const renderActiveTabContent = useCallback(() => {
    if (!activeTabRenderData) return null

    const { tab, historyItem } = activeTabRenderData

    if (historyItem.type === 'document') {
      return renderDocumentTab(tab, historyItem)
    }

    // Search and time-series tabs are rendered by the always-mounted keep-alive
    // layers below so their in-flight work and local state survive tab switches.
    return null
  }, [activeTabRenderData, renderDocumentTab])

  useEffect(() => {
    const openSearchTabIds = new Set(
      openTabs
        .filter((tab) => tab.history[tab.currentIndex]?.type === 'search')
        .map((tab) => tab.id)
    )
    const activeSearchTabId = openSearchTabIds.has(activeTabId)
      ? activeTabId
      : undefined

    setMountedSearchTabIds((prev) => {
      const next = new Set(
        [...prev].filter((tabId) => openSearchTabIds.has(tabId))
      )
      if (activeSearchTabId) {
        next.add(activeSearchTabId)
      }
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) {
        return prev
      }
      return next
    })
  }, [openTabs, activeTabId])

  const renderKeepAliveSearchTabs = useCallback(
    () =>
      openTabs
        .filter(
          (tab) =>
            tab.history[tab.currentIndex]?.type === 'search' &&
            (mountedSearchTabIds.has(tab.id) || tab.id === activeTabId)
        )
        .map((tab) => {
          const item = tab.history[tab.currentIndex] as SearchHistoryItem
          const linkedDocuments = item.linkedDocuments
          const isActive = tab.id === activeTabId
          return (
            <KeepAlivePanel key={tab.id} $active={isActive}>
              <AdvancedSearchPage
                siteId={siteID}
                tabId={tab.id}
                isActive={isActive}
                searchState={item.searchState}
                linkedDocIds={linkedDocuments?.documentIds}
                unitId={linkedDocuments?.unitId}
              />
            </KeepAlivePanel>
          )
        }),
    [openTabs, mountedSearchTabIds, activeTabId, siteID]
  )

  // A chart saved from an embedded tab is a real (saved) chart from that
  // moment on: record it in the shared recents, and upgrade the tab's history
  // item in place so the tab is chart-backed from now on — the label shows the
  // chart name, and a restored tab re-renders the saved chart with full
  // fidelity. (The mounted provider already saves to the promoted id, so no
  // remount/prop-flip is needed for subsequent saves.)
  //
  // Two-way name sync: the chart name is authoritative, so rename the tab to
  // match on every save (renameTab sets customLabel, which wins over the
  // history-item label a manual rename would otherwise mask). Fires on every
  // save so the tab and chart stay in lockstep, not just at first save.
  const handleEmbeddedChartSaved = useCallback(
    (tabId: string, info: EmbeddedChartSavedInfo) => {
      const name = chartSaveTabLabel(info.name)
      const chartItem: ChartSearchItem = {
        id: info.chartID,
        itemType: 'chart',
        displayName: name,
        name,
        sensorIds: info.sensorIds,
      }
      addRecentItem(chartItem)
      updateTimeSeriesHistoryItem(tabId, { chartID: info.chartID, label: name })
      renameTab(tabId, name)
    },
    [addRecentItem, updateTimeSeriesHistoryItem, renameTab]
  )

  // Time-series tabs stay mounted (overlaid, hidden when inactive) so their
  // chart selection/zoom survives switching to another tab and back.
  const renderKeepAliveTimeSeriesTabs = useCallback(
    () =>
      openTabs
        .filter((tab) => tab.history[tab.currentIndex]?.type === 'timeseries')
        .map((tab) => {
          const item = tab.history[tab.currentIndex] as TimeSeriesHistoryItem
          return (
            <KeepAlivePanel key={tab.id} $active={tab.id === activeTabId}>
              <EmbeddedTimeSeriesChart
                sensorIds={item.sensorIds}
                siteID={siteID}
                tabId={tab.id}
                initialTimeRange={item.timeWindow}
                autoOpenTagSearch={item.autoOpenTagSearch}
                sensorColors={item.sensorColors}
                deemphasizedSensorIds={item.deemphasizedSensorIds}
                controlled={item.controlled}
                savedChartID={item.chartID}
                // Pre-fill the first-save name prompt with the tab's current
                // display name (customLabel wins, else the history-item label).
                defaultChartName={timeSeriesTabDefaultName(tab, item)}
                onChartSaved={(info) => handleEmbeddedChartSaved(tab.id, info)}
              />
            </KeepAlivePanel>
          )
        }),
    [openTabs, activeTabId, siteID, handleEmbeddedChartSaved]
  )

  useEffect(() => {
    persistChatbotState(siteID, isChatbotOpen)
  }, [siteID, isChatbotOpen])

  const documentTabTitle = useMemo(() => {
    if (!currentDocumentId || !attachmentDoc) return undefined
    const filename = attachmentDoc.entry.data?.filename ?? currentDocumentId
    return (
      attachmentDoc.entry.data?.document_title ??
      attachmentDoc.entry.data?.title ??
      filename
    )
  }, [attachmentDoc, currentDocumentId])

  // When leaving a document, republish the history-derived search label so
  // customLabel does not keep the document title after back navigation.
  // Ephemeral home defaults are published as null so they are not stored as
  // customLabel (which would block a later document title / recent-item title).
  const searchTabTitle = useMemo(() => {
    if (currentDocumentId) return undefined
    const activeTab = openTabs.find((tab) => tab.id === activeTabId)
    if (!activeTab) return undefined
    const label = getWorkspaceTabLabel({ ...activeTab, customLabel: undefined })
    if (label === 'New Tab' || label === 'New Search') return null
    return label
  }, [activeTabId, currentDocumentId, openTabs])

  useWorkspaceTabTitle(documentTabTitle ?? searchTabTitle)

  useEffect(() => {
    const selectedDocument = attachmentDoc
    if (!currentDocumentId || !selectedDocument) {
      return
    }

    const filename = selectedDocument.entry.data?.filename ?? currentDocumentId
    const displayTitle =
      selectedDocument.entry.data?.document_title ??
      selectedDocument.entry.data?.title ??
      filename

    addAttachment({
      id: currentDocumentId,
      type: 'system_document',
      name: filename,
      displayTitle,
    })
  }, [currentDocumentId, attachmentDoc, addAttachment])

  useEffect(() => {
    const currentTabIds = new Set(openTabs.map((t) => t.id))
    for (const tabId of searchResultsByTabRef.current.keys()) {
      if (!currentTabIds.has(tabId)) {
        searchResultsByTabRef.current.delete(tabId)
      }
    }
    for (const tabId of intraSearchStateByTabRef.current.keys()) {
      if (!currentTabIds.has(tabId)) {
        intraSearchStateByTabRef.current.delete(tabId)
      }
    }
  }, [openTabs])

  return (
    <SearchActionsProvider
      recentItems={recentItems}
      addRecentItem={addRecentItem}
      onOpenChatbot={() => setIsChatbotOpen(true)}
      onSnapshotSearchResults={handleSnapshotSearchResults}
      isFavorited={isFavorited}
      toggleFavorite={toggleFavorite}>
      <EditorContainer>
        <EditorMainColumn>
          {!workspaceTabs && <DocumentTabsBar />}
          <MainContentWrapper>
            <DocumentContentWrapper>
              <ViewerContainer>
                <TabTransitionOverlay key={`tab-transition-${activeTabId}`} />
                {renderActiveTabContent()}
                {renderKeepAliveSearchTabs()}
                {renderKeepAliveTimeSeriesTabs()}
              </ViewerContainer>
            </DocumentContentWrapper>
          </MainContentWrapper>
        </EditorMainColumn>

        <ChatbotSidePanel
          isOpen={isChatbotOpen}
          onClose={() => setIsChatbotOpen(false)}
        />

        <ContextMenu
          position={contextMenuState?.position ?? null}
          onClose={contextMenuState?.onClose ?? (() => {})}
          onOpen={contextMenuState?.onOpen ?? (() => {})}
          onOpenInNewTab={contextMenuState?.onOpenInNewTab ?? (() => {})}
          onAddToContext={contextMenuState?.onAddToContext ?? (() => {})}
          onAddToExistingDashboard={
            contextMenuState?.onAddToExistingDashboard ?? (() => {})
          }
          showNavigationOptions={
            contextMenuState?.showNavigationOptions ?? false
          }
          showAddToContext={contextMenuState?.showAddToContext ?? false}
          existingDashboardTabs={contextMenuState?.existingDashboardTabs ?? []}
        />
      </EditorContainer>
    </SearchActionsProvider>
  )
}
